import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProductsPage } from '../apis';
import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import { useRealtimeEvent } from '../hooks/useRealtimeEvent';
import faceMaskBanner from "../assests/facemask.png";
import pliers from "../assests/pliers.png";
import brackets from "../assests/brackets.png"
import wires from "../assests/wires.png";
import bandstubes from "../assests/bandstubes.png"
import elastomerics from "../assests/elastomerics.png"


const categoryBanners = {
  brackets: {
    title: 'Explore our premium',
    highlight: 'Brackets',
    image: brackets,
    description: 'High-quality orthodontic brackets that ensure comfort and reliability for professionals.',
  },
  wires_and_springs: {
    title: 'Top-grade',
    highlight: 'Wires & Springs',
    image: wires,
    description: 'Durable and flexible wires & springs for efficient orthodontic treatment.'
  },
  bands_and_tubes: {
    title: 'Reliable',
    highlight: 'Bands & Tubes',
    image: bandstubes,
    description: 'Sturdy bands and tubes built for performance and patient comfort.',
  },
  orthodontic_pliers: {
    title: 'Precision tools:',
    highlight: 'Orthodontic Pliers',
    image: pliers,
    description: 'Get the best results with our finely crafted orthodontic pliers.'
  },
  elastomerics: {
    title: 'Elastic solutions:',
    highlight: 'Elastomerics',
    image: elastomerics,
    description: 'Elastic materials that adapt and perform with consistency.'
  },
  miscellaneous: {
    title: 'Explore',
    highlight: 'Miscellaneous Tools',
    image: faceMaskBanner,
    description: 'Supportive tools and items to complete your orthodontic kit.'
  }
};

const PRODUCTS_PER_PAGE = 24;

const SpecificCategory = () => {
  const params = useParams();
  const [route, setRoute] = useState(params.category);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  // Tracks the category a fetch was last kicked off for, so a category
  // change can reset to page 1 without also firing a stray fetch for the
  // old (about-to-be-replaced) page number first.
  const lastCategoryRef = useRef(params.category);
  // Bumped by the realtime subscription below to force the fetch effect to
  // re-run (e.g. a product's price changed) without altering category/page.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const formattedStr = params.category.replace(/_/g, ' ').replace(/ and /gi, ' & ');
    setRoute(formattedStr);
  }, [params.category]);

  useEffect(() => {
    const categoryChanged = lastCategoryRef.current !== params.category;
    lastCategoryRef.current = params.category;

    // Category just changed and we're not already on page 1 — reset page
    // state and let the resulting re-run of this effect do the actual
    // fetch, instead of fetching once for the stale page and again for
    // page 1.
    if (categoryChanged && page !== 1) {
      setPage(1);
      return;
    }

    let cancelled = false;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await getProductsPage({
          category: params.category,
          page,
          limit: PRODUCTS_PER_PAGE,
        });
        if (cancelled) return;
        if (response.success) {
          setProducts(response.data || []);
          setTotalPages(response.totalPages || 1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProducts();

    return () => {
      cancelled = true;
    };
  }, [params.category, page, refreshTick]);

  // Live sync with the admin panel — see frontend/src/context/MainContext.js
  // for the matching subscription that covers the homepage/product-detail
  // views built from MainContext instead of this page's own fetch.
  useRealtimeEvent("catalog:changed", (data) => {
    if (!data.resource || data.resource === "product" || data.resource === "category") {
      setRefreshTick((t) => t + 1);
    }
  });

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const banner = categoryBanners[params.category];

  return (
    <div className="min-h-screen bg-white py-6 px-14">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center capitalize">
        {route}
      </h1>

      {banner && (
        <div className="bg-gradient-to-r from-[#E4E4FF] to-white rounded-xl shadow-md px-6 py-10 mb-10 grid md:grid-cols-2 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-grey-800 mb-3">
                {banner.title} <span className="text-[#2D3092]">{banner.highlight}</span>
              </h2>
              <p className="text-gray-600 text-base sm:text-lg mb-4 font-semibold">{banner.description}</p>
            </div>
            <div className="flex justify-center">
              <img src={banner.image} alt="Brackets" className="h-40 md:h-56 object-contain" />
            </div>  
        </div>
      )}
      {loading ? (
        <div className="text-center mt-10 text-lg">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:justify-center sm:gap-6">
            {products.map((item, index) => (
              <ProductCard key={item.id || index} data={item} />
            ))}
          </div>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </>
      )}
    </div>
  );
};

export default SpecificCategory;
